import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { ShieldCheck, X } from 'lucide-react';
import { CONSENT_COPY } from '../../data/consentI18n';
import { getPageMetadata } from '../../data/pageMetadata';
import { useConsent } from './ConsentProvider';

function useConsentCopy() {
  const { pathname } = useLocation();
  return CONSENT_COPY[getPageMetadata(pathname).lang] || CONSENT_COPY.en;
}

export function ConsentSettingsButton() {
  const L = useConsentCopy();
  const { openSettings } = useConsent();
  return <button type="button" onClick={openSettings} data-testid="consent-settings"
    className="mt-3 underline underline-offset-4 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">
    {L.settings}
  </button>;
}

const actionClass = 'rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand)]';

export default function ConsentBanner() {
  const L = useConsentCopy();
  const { consent, preferencesAllowed, analyticsAllowed, settingsOpen, setSettingsOpen, openSettings, restoreFocus, choose } = useConsent();
  const [draft, setDraft] = useState(false);
  const [analyticsDraft, setAnalyticsDraft] = useState(analyticsAllowed);
  const [error, setError] = useState(false);
  const bannerRef = useRef(null);
  const [bannerHeight, setBannerHeight] = useState(0);
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  const visible = mounted && (!consent || consent.version === 1);

  useEffect(() => {
    if (settingsOpen) {
      setDraft(preferencesAllowed);
      setAnalyticsDraft(analyticsAllowed);
    }
  }, [settingsOpen, preferencesAllowed, analyticsAllowed]);

  useEffect(() => {
    if (settingsOpen) setError(false);
  }, [settingsOpen]);

  // Reserve the banner's height so it never hides footer links on small screens.
  useEffect(() => {
    if (!visible || !bannerRef.current) { setBannerHeight(0); return; }
    const observer = new ResizeObserver(([entry]) => setBannerHeight(entry.target.offsetHeight));
    observer.observe(bannerRef.current);
    return () => observer.disconnect();
  }, [visible]);

  const save = (preferences, analytics = false) => setError(!choose(preferences, analytics));
  return <>
    {visible && <>
      <div aria-hidden="true" style={{ height: bannerHeight }} />
      <section ref={bannerRef} aria-labelledby="consent-banner-title" data-testid="consent-banner"
        className="fixed inset-x-0 bottom-0 z-[60] max-h-[65dvh] overflow-y-auto border-t border-[var(--line)] bg-[var(--cream)] shadow-[0_-8px_30px_rgba(15,23,42,0.08)]">
        <div className="mx-auto max-w-[1100px] px-5 py-5 sm:px-7">
          <h2 id="consent-banner-title" className="flex items-center gap-2 text-lg font-semibold text-slate-900"><ShieldCheck aria-hidden="true" className="h-5 w-5 shrink-0 text-[var(--brand)]" />{L.title}</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">{L.intro}</p>
          <p className="mt-1 text-xs leading-relaxed text-slate-600">{L.optional} {L.duration}</p>
          <div className="mt-4 grid gap-2 sm:flex sm:flex-wrap">
            <button type="button" className={actionClass} data-testid="consent-reject" onClick={() => save(false, false)}>{L.reject}</button>
            <button type="button" className={actionClass} data-testid="consent-customize" onClick={openSettings}>{L.customize}</button>
            <button type="button" className={actionClass} data-testid="consent-accept" onClick={() => save(true, true)}>{L.accept}</button>
          </div>
          {error && !settingsOpen && <p role="alert" className="mt-3 text-sm text-red-800">{L.error}</p>}
        </div>
      </section>
    </>}
    <Dialog.Root open={settingsOpen} onOpenChange={setSettingsOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[80] bg-slate-900/40" />
        <Dialog.Content onCloseAutoFocus={(event) => { event.preventDefault(); restoreFocus(); }}
          className="fixed left-1/2 top-1/2 z-[90] max-h-[85dvh] w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-[var(--cream)] p-6 shadow-xl" data-testid="consent-dialog">
          <Dialog.Title className="pr-8 text-xl font-semibold text-slate-900">{L.settings}</Dialog.Title>
          <Dialog.Description className="mt-3 text-sm leading-relaxed text-slate-700">{L.optional} {L.duration}</Dialog.Description>
          <div className="mt-5 rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-900">{L.required}</h3>
            <p className="mt-1 text-xs font-medium text-slate-600">{L.always}</p>
            <p className="mt-2 text-sm text-slate-600">{L.requiredDesc}</p>
          </div>
          <div className="mt-3 rounded-xl border border-slate-200 p-4">
            <label className="flex cursor-pointer items-center justify-between gap-4 font-semibold text-slate-900">
              {L.preferences}
              <input type="checkbox" checked={draft} onChange={(event) => setDraft(event.target.checked)} aria-describedby="consent-preferences-description" data-testid="consent-preferences" className="h-5 w-5 shrink-0 accent-[var(--brand)]" />
            </label>
            <p id="consent-preferences-description" className="mt-2 text-sm text-slate-600">{L.preferencesDesc}</p>
          </div>
          <div className="mt-3 rounded-xl border border-slate-200 p-4">
            <label className="flex cursor-pointer items-center justify-between gap-4 font-semibold text-slate-900">
              {L.analytics}
              <input type="checkbox" checked={analyticsDraft} onChange={(event) => setAnalyticsDraft(event.target.checked)} aria-describedby="consent-analytics-description" data-testid="consent-analytics" className="h-5 w-5 shrink-0 accent-[var(--brand)]" />
            </label>
            <p id="consent-analytics-description" className="mt-2 text-sm text-slate-600">{L.analyticsDesc}</p>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <button type="button" className={actionClass} onClick={() => save(false, false)} data-testid="consent-dialog-reject">{L.reject}</button>
            <button type="button" className={actionClass} onClick={() => save(draft, analyticsDraft)} data-testid="consent-save">{L.save}</button>
          </div>
          {error && <p role="alert" className="mt-3 text-sm text-red-800">{L.error}</p>}
          <Dialog.Close aria-label={L.close} className="absolute right-4 top-4 rounded p-1 text-slate-700 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2"><X aria-hidden="true" className="h-5 w-5" /></Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  </>;
}
